import EmberService, { service } from '@ember/service';

import type { Service } from 'webapp-types';
import { repeatArrayItems, sortBy, uniq } from 'webapp/utils/common';

import BoardLoopsPopupPromoComponent from 'webapp/components/board-loops/popup-promo';

import type Template from 'webapp/models-custom/template';

export default class FlowBoardLoopPromoService extends EmberService {
	@service('popup') declare popupService: Service.Popup;
	@service('user-settings') declare userSettingsService: Service.UserSettings;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;

	get repeatedSourceTypeKeys() {
		const connectedSourceTypesKeys = uniq(
			this.sourceModels.map((source) => source.sourceType.key),
		);

		return repeatArrayItems<string>(connectedSourceTypesKeys, 3);
	}

	get sourceModels() {
		return sortBy(this.mmSourcesService.connectedCloudSourceModels, 'sourceTypePopularity');
	}

	get sourceTypeAvatars() {
		const repeatedSourceTypeKeys = uniq(this.repeatedSourceTypeKeys);
		return this.sourceModels
			.filter((source) => repeatedSourceTypeKeys.includes(source.sourceType.key))
			.map((source) => source.sourceType.avatar);
	}

	checkToOpenPopup(): void {
		if (
			!this.popupService.anyPopupsOpen &&
			this.billingService.isActiveTrial &&
			!this.billingService.whiteLabelingEnabled &&
			this.mmSourcesService.connectedCloudSourceModels.length >= 2 &&
			!this.userSettingsService.hasSeenLoopPromoPopup
		) {
			void this.mmTemplatesService.requestBoardPublicTemplates().finally(() => {
				const usedTemplatesIds = uniq(
					this.mmBoardsService.boardModels.map((board) => board.originateFrom),
				);

				const popularTemplatesPerSource: Template[] = [],
					repeatedSourceTypesKeys = this.repeatedSourceTypeKeys,
					unusedTemplates =
						this.mmTemplatesService.popularBoardPublicTemplateModels.filter(
							(template) => !usedTemplatesIds.includes(template.objectId),
						);

				repeatedSourceTypesKeys.forEach((sourceTypeKey) => {
					const newTemplate = unusedTemplates.find(
						(template) =>
							template.specificSourceTypesKeys.includes(sourceTypeKey) &&
							!popularTemplatesPerSource.some(
								(popularTemplate) =>
									popularTemplate.numericId === template.numericId,
							),
					);

					if (newTemplate) {
						popularTemplatesPerSource.push(newTemplate);
					}
				});

				if (popularTemplatesPerSource.length >= 2) {
					const popupBoardLoopsPromo = this.popupService.createModal(
						BoardLoopsPopupPromoComponent,
						{
							popupClass: 'popup-type-board-loop-promo',
						},
					);

					popupBoardLoopsPromo.open({
						model: popularTemplatesPerSource,
						sourceTypeAvatars: this.sourceTypeAvatars,
						sources: this.sourceModels,
					});
				}
			});
		}
	}
}

declare module '@ember/service' {
	interface Registry {
		'flow/board-loop-promo': FlowBoardLoopPromoService;
	}
}
